import { useState, useRef, useEffect, useCallback } from "react";

import {
  Excalidraw,
  MainMenu,
  WelcomeScreen,
  loadLibraryFromBlob,
} from "@excalidraw/excalidraw";
import debounce from "lodash/debounce";
import { EyeIcon, Trash2 } from "lucide-react";

import { UserRole } from "@/types";

import tutredLogo from "../../assets/images/logo.svg";
import useLesson from "../../contexts/useLesson";
import useFirebaseContext from "../../hooks/useFirebaseAuth";
import { useSocketIO } from "../../hooks/useSocketIO";
import useVideoContext from "../../hooks/useVideoContext";
import useWhiteboardRecording from "../../hooks/useWhiteboardRecording";
import { whiteboardCache } from "../../utils/whiteboardCache";

import { CollabSocketIO } from "./CollabSocketIO";
import mathTeacherLibraryRaw from "./libraries/math-teacher-library.excalidrawlib?raw";
import organicChemistryLibraryRaw from "./libraries/organic-chemistry-basics.excalidrawlib?raw";
import scienceChemistryLibraryRaw from "./libraries/science-chemistry-gcse.excalidrawlib?raw";

const excalidrawStyles = `
  .excalidraw .layer-ui__library {
    height: calc(100% - 184px) !important;
    max-height: calc(100% - 184px) !important;
    overflow-y: auto !important;
  }
`;

function Whiteboard({ userRole }: { userRole: UserRole }) {
  const [excalidrawAPI, setExcalidrawAPI] = useState<any>(null);
  const { room, onError } = useVideoContext();
  const { lesson } = useLesson();
  const { user } = useFirebaseContext();
  const isTutor = userRole === UserRole.Tutor;
  const collabSocketIORef = useRef<any>(null);
  const [studentsViewMode, setStudentsViewMode] = useState(false);
  
  // Use Socket.IO for whiteboard collaboration (only method)
  const { socket, isConnected: isSocketConnected } = useSocketIO({
    lessonId: lesson?.id || null,
    enabled: true,
  });
  
  // Determine username from user or lesson
  const username = user?.displayName || user?.email || 
    (isTutor ? lesson?.tutorName : lesson?.studentName) || 
    "Unknown";

  const userId = user?.uid || `${userRole}-${username}`;
  useWhiteboardRecording({
    room,
    onError,
    frameRate: 30,
    recordingEnabled: lesson?.recordingEnabled || false
  });

  useEffect(() => {
    const initializeLibraries = async () => {
      try {
        const libraries = [
          new Blob([mathTeacherLibraryRaw], { type: "application/json" }),
          new Blob([scienceChemistryLibraryRaw], { type: "application/json" }),
          new Blob([organicChemistryLibraryRaw], { type: "application/json" }),
        ];

        const libraryPromises = libraries.map((library) =>
          loadLibraryFromBlob(library)
        );
        const libraryItems = await Promise.all(libraryPromises);

        await excalidrawAPI.updateLibrary({
          libraryItems: libraryItems.flat(),
          merge: true,
          openLibraryMenu: false,
        });
      } catch (error) {
        console.error("Failed to load libraries:", error);
      }
    };

    if (excalidrawAPI) {
      initializeLibraries();
    }
  }, [excalidrawAPI]);

  const toggleViewMode = () => {
    if (!excalidrawAPI || !socket || !isSocketConnected) {
      console.warn("[Whiteboard] Cannot toggle view mode - Socket.IO not connected");
      return;
    }
    
    socket.emit("whiteboard:view-mode-update", {
      type: "view-mode-update",
      viewModeEnabled: !studentsViewMode,
    });
    setStudentsViewMode(!studentsViewMode);
  };

  const handleViewModeUpdate = useCallback((viewModeEnabled: boolean) => {
    setStudentsViewMode(viewModeEnabled);
  }, []);

  const debouncedSaveToCache = useCallback(
    debounce((elements: readonly any[], appState: any) => {
      if (lesson?.id) {
        const { collaborators: _collaborators, ...cachedAppState } = appState;
        
        if (elements.length > 0) {
          whiteboardCache.setElements(lesson.id, Array.from(elements), cachedAppState);
        } else {
          whiteboardCache.setAppState(lesson.id, cachedAppState);
        }
      }
    }, 1000), 
    [lesson?.id]
  );

  const handleExcalidrawChange = (elements: readonly any[], appState: any, _files: any) => {
    debouncedSaveToCache(elements, appState);
  };

  const handleEraseAll = () => {
    if (!excalidrawAPI) {
      console.warn("[Whiteboard] Cannot erase all - Excalidraw API not available");
      return;
    }

    if (!socket || !isSocketConnected) {
      console.warn("[Whiteboard] Socket not connected - changes may not sync");
      return;
    }

    if (confirm("Are you sure you want to erase all, this action cannot be undone?")) {
      const currentAppState = excalidrawAPI.getAppState();
      
      // Clear all elements
      excalidrawAPI.updateScene({ 
        elements: [], 
        appState: currentAppState 
      });
      // Clear the cache
      if (lesson?.id) {
        whiteboardCache.setElements(lesson.id, [], currentAppState);
      }

      socket.emit("whiteboard:erase-all", {
        type: "erase-all",
      });
    }
  };

  useEffect(() => {
    return () => {
      debouncedSaveToCache.cancel();
    };
  }, [debouncedSaveToCache]);

  return (
    <div className="whiteboard-container h-full w-full">
      <style>{excalidrawStyles}</style>
      <Excalidraw
        excalidrawAPI={(api) => setExcalidrawAPI(api)}
        isCollaborating={true}
        initialData={{
          elements: lesson?.id ? whiteboardCache.getElements(lesson.id) || [] : [],
          appState: lesson?.id ? {
            ...whiteboardCache.getAppState(lesson.id),
            collaborators: new Map(),
            viewBackgroundColor: whiteboardCache.getAppState(lesson.id)?.viewBackgroundColor || "#ffffff"
          } : { 
            viewBackgroundColor: "#ffffff",
            collaborators: new Map()
          },
          scrollToContent: true,
        }}
        onChange={handleExcalidrawChange}
        onPointerUpdate={collabSocketIORef.current?.onPointerUpdate}
        
      >
        {socket && excalidrawAPI && (
          <CollabSocketIO
            ref={collabSocketIORef}
            excalidrawAPI={excalidrawAPI}
            socket={socket}
            lessonId={lesson?.id || null}
            username={username}
            userId={userId}
            onViewModeUpdate={handleViewModeUpdate}
          />
        )}
        <WelcomeScreen>
          <WelcomeScreen.Center>
            <WelcomeScreen.Center.Logo>
              <img
                src={tutredLogo}
                alt="Tutred"
                style={{
 width: "500px",
height: "auto",
opacity: 0.75 
}}
              />
            </WelcomeScreen.Center.Logo>
            <WelcomeScreen.Center.Heading>
              Learning made easy!
            </WelcomeScreen.Center.Heading>
          </WelcomeScreen.Center>
          <WelcomeScreen.Hints.ToolbarHint></WelcomeScreen.Hints.ToolbarHint>
          <WelcomeScreen.Hints.MenuHint />
        </WelcomeScreen>
        <MainMenu>
          <MainMenu.DefaultItems.LoadScene />
          <MainMenu.DefaultItems.Export />
          <MainMenu.DefaultItems.SaveAsImage />
          <MainMenu.DefaultItems.Help />
          <MainMenu.Separator />
          {isTutor && (
            <MainMenu.Item onSelect={toggleViewMode} icon={<EyeIcon />}>
              {studentsViewMode
                ? "Allow Student Collaboration"
                : "Disable Student Collaboration"}
            </MainMenu.Item>
          )}
          {isTutor && (
            <>
            <MainMenu.Item onSelect={handleEraseAll} icon={<Trash2 />}>
              Erase All
            </MainMenu.Item>
            </>
          )}
          <MainMenu.DefaultItems.ChangeCanvasBackground />
        </MainMenu>
      </Excalidraw>
    </div>
  );
}

export default Whiteboard;
